'use client'

import { Pencil24, Plus24 } from '@vinted/monochrome-icons'
import { Cell, Icon, Label, Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { AddressModel, AddressType } from '@marketplace-web/address/address-data'

import EditAddressModal from '../../../BalanceActivationModal/EditAddressModal'
import { PROFILE_SETTINGS_URL } from '../../../../constants/routes'
import { Field } from '../../types'
import AddressDetails from '../../../AddressDetails'

type Props = {
  isDisabled: boolean
  externalModal?: boolean
  onClick?: () => void
  address: AddressModel | null
  addressType?: AddressType
  setAddress: (address: AddressModel | null) => void
}

const BillingAddressDetails = ({
  isDisabled,
  externalModal,
  onClick,
  address: billingAddress,
  addressType,
  setAddress,
}: Props) => {
  const translate = useTranslate('wallet.activation.billing_address')
  const recidencyTranslate = useTranslate('wallet.activation.residency_address')

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<{
    [Field.Address]: AddressModel | null
  }>()

  const validationMessage = useFormValidationMessage(
    errors,
    addressType === AddressType.Residency
      ? 'wallet.activation.residency_address.fields'
      : 'wallet.activation.billing_address.fields',
  )

  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)
  const address = watch(Field.Address)

  const { user } = useSession()

  const isBusinessAccount = !!user?.business

  useEffect(() => {
    register(Field.Address, { required: true })
  }, [register])

  function handleAddressChanged(onChange: (...event: Array<unknown>) => void) {
    setAddress(address)

    return (adress: AddressModel) => {
      onChange(adress)
      setIsAddressModalVisible(false)
    }
  }

  function showChangeAddressModal() {
    setIsAddressModalVisible(true)
  }

  function hideChangeAddressModal() {
    setIsAddressModalVisible(false)
  }

  function renderEmptyAddress() {
    const onClickHandler = externalModal ? onClick : showChangeAddressModal

    const title =
      addressType === AddressType.Residency
        ? recidencyTranslate('add_address')
        : translate('add_address')

    return (
      <Cell
        styling={Cell.Styling.Wide}
        type={Cell.Type.Navigating}
        suffix={<Icon name={Plus24} aria={{ 'aria-hidden': 'true' }} />}
        title={title}
        onClick={onClickHandler}
        disabled={isDisabled}
        validation={renderValidation(validationMessage(Field.Address))}
        testId="billing-address-details--add-address-button"
      />
    )
  }

  function renderAddressDetails() {
    const onClickHandler = externalModal ? onClick : showChangeAddressModal

    if (isBusinessAccount) {
      const translationValues = {
        'profile-link': (chunks: Array<ReactNode>) => (
          <a key="profile-link" href={PROFILE_SETTINGS_URL}>
            {chunks}
          </a>
        ),
      }

      const validation = (
        <>
          <Spacer size={Spacer.Size.XLarge} />
          <Text as="h4" type={Text.Type.Caption}>
            {translate('business_note', translationValues)}
          </Text>
        </>
      )

      return (
        <AddressDetails styling={Cell.Styling.Wide} address={address} validation={validation} />
      )
    }

    return (
      <AddressDetails
        styling={Cell.Styling.Wide}
        type={Cell.Type.Navigating}
        address={address}
        suffix={!isDisabled && <Icon name={Pencil24} aria={{ 'aria-hidden': 'true' }} />}
        isDisabled={isDisabled}
        onClick={onClickHandler}
        validation={renderValidation(validationMessage(Field.Address))}
      />
    )
  }

  function renderEditAddressModal(onChange: (...event: Array<unknown>) => void) {
    return (
      <EditAddressModal
        address={address}
        isOpen={isAddressModalVisible}
        onChange={handleAddressChanged(onChange)}
        onCancel={hideChangeAddressModal}
        addressType={addressType}
      />
    )
  }

  function renderBillingAddress() {
    return billingAddress ? renderAddressDetails() : renderEmptyAddress()
  }

  const labelText =
    addressType === AddressType.Residency ? recidencyTranslate('title') : translate('title')

  return (
    <Controller
      control={control}
      name={Field.Address}
      render={({ field: { onChange } }) => (
        <>
          <Label styling={Label.Styling.Wide} type={Label.Type.Leading} text={labelText} />
          {renderBillingAddress()}
          {!isBusinessAccount && renderEditAddressModal(onChange)}
        </>
      )}
    />
  )
}

export default BillingAddressDetails
